import { useState } from "react";
import api from "@/lib/api";
import { leadValue, rawValue } from "@/components/crm/LeadViews";
import { toast } from "sonner";
import { ExternalLink, Copy } from "lucide-react";

const READONLY = ["source", "created_at", "score"];

function Extra({ f, v }) {
  const s = String(v ?? "").trim();
  if (!s) return null;
  const cls = "shrink-0 p-1 rounded text-[var(--text-dim)] hover:text-[var(--text)] hover:bg-[var(--hover)]";
  if (f.type === "url" || /^https?:\/\//i.test(s) || (f.type !== "textarea" && /^www\./i.test(s))) {
    const href = /^https?:\/\//i.test(s) ? s : `https://${s}`;
    return <a className={cls} href={href} target="_blank" rel="noreferrer" title="Open link" data-testid={`open-link-${f.key}`}><ExternalLink size={13} /></a>;
  }
  if (f.type === "phone" || f.key === "phone") {
    const copy = async () => { try { await navigator.clipboard.writeText(s); toast.success("Phone copied"); } catch { toast.error("Couldn't copy"); } };
    return <button type="button" className={cls} onClick={copy} title="Copy phone" data-testid={`copy-${f.key}`}><Copy size={13} /></button>;
  }
  return null;
}

function Editor({ f, value, onSave, onCancel, stages, users }) {
  const [v, setV] = useState(value ?? "");
  const cls = "w-full px-2 py-1 rounded border text-sm";
  const done = () => onSave(f.type === "number" && v !== "" ? Number(v) : v);
  const keys = { onKeyDown: (e) => { if (e.key === "Enter" && f.type !== "textarea") done(); if (e.key === "Escape") onCancel(); }, autoFocus: true, "data-testid": `inline-input-${f.key}` };
  const opts = f.type === "stage" ? stages : f.type === "owner" ? users.map((u) => u.email) : f.type === "select" ? f.options || [] : null;
  if (opts) return (
    <select className={cls} value={v} onChange={(e) => onSave(e.target.value)} onBlur={onCancel} {...keys}>
      <option value="">—</option>{opts.map((o) => <option key={o} value={o}>{f.type === "owner" ? users.find((u) => u.email === o)?.name || o : o}</option>)}
    </select>
  );
  if (f.type === "textarea") return <textarea rows={3} className={cls} value={v} onChange={(e) => setV(e.target.value)} onBlur={done} {...keys} />;
  const type = { number: "number", date: "date", email: "email" }[f.type] || "text";
  return <input type={type} className={cls} value={f.type === "date" ? String(v).slice(0, 10) : v} onChange={(e) => setV(e.target.value)} onBlur={done} {...keys} />;
}

export default function LeadFields({ lead, fields, stages, users, onSaved, wide }) {
  const [editing, setEditing] = useState(null);
  const [showEmpty, setShowEmpty] = useState(false);
  const [closed, setClosed] = useState({});
  const empty = (f) => leadValue(lead, f, users) === "—";
  const emptyCount = fields.filter(empty).length;
  const visible = fields.filter((f) => showEmpty || !empty(f) || editing === f.key);
  const sections = [...new Set(visible.map((f) => f.section || "Details"))];
  const save = async (f, v) => {
    setEditing(null);
    if (v === (rawValue(lead, f.key) ?? "")) return;
    const builtin = f.builtin;
    const body = builtin ? { [f.key]: v === "" && f.type === "number" ? 0 : v } : { custom: { [f.key]: v } };
    try { await api.put(`/crm/leads/${lead._id}`, body); toast.success(`${f.label} updated`); onSaved(); }
    catch (e) { toast.error(e.response?.data?.detail?.[0]?.msg || "Save failed"); }
  };
  return (
    <div className="space-y-4" data-testid="lead-fields">
      {sections.map((s) => (
        <section key={s} data-testid={`lead-section-${s}`}>
          <button type="button" onClick={() => setClosed((c) => ({ ...c, [s]: !c[s] }))} className="section-label mb-1 flex items-center gap-1 hover:text-[var(--text)]" data-testid={`lead-section-toggle-${s}`}>
            <span className={`inline-block transition-transform ${closed[s] ? "-rotate-90" : ""}`}>▾</span>{s}
          </button>
          {!closed[s] && <dl className={`grid grid-cols-1 ${wide ? "md:grid-cols-2 xl:grid-cols-1 2xl:grid-cols-2 gap-x-8" : ""}`}>
            {visible.filter((f) => (f.section || "Details") === s).map((f) => (
              <div key={f.key} className={`grid ${wide ? "grid-cols-[130px_1fr] min-h-[34px] border-b border-[var(--border)]/50" : "grid-cols-[104px_1fr] min-h-[30px]"} items-start py-1 gap-2`}>
                <dt className="text-[13px] text-[var(--text-dim)] break-words self-start pt-1">{f.label}</dt>
                <dd className="text-sm min-w-0 flex items-start gap-1">
                  {editing === f.key ? <Editor f={f} value={rawValue(lead, f.key)} stages={stages} users={users} onSave={(v) => save(f, v)} onCancel={() => setEditing(null)} />
                    : (
                      <>
                      <button type="button" disabled={READONLY.includes(f.key)} onClick={() => setEditing(f.key)} data-testid={`inline-${f.key}`}
                        className="flex-1 min-w-0 text-left break-words whitespace-pre-line px-1.5 py-0.5 -mx-1.5 rounded text-[var(--text)] hover:bg-[var(--hover)] disabled:hover:bg-transparent disabled:cursor-default">
                        {leadValue(lead, f, users) === "—" ? <span className="text-[var(--text-faint)]">—</span> : leadValue(lead, f, users)}
                      </button>
                      <Extra f={f} v={rawValue(lead, f.key)} />
                      </>
                    )}
                </dd>
              </div>
            ))}
          </dl>}
        </section>
      ))}
      {emptyCount > 0 && <button type="button" onClick={() => setShowEmpty((v) => !v)} className="text-xs text-[var(--ai)] hover:underline" data-testid="lead-fields-empty-toggle">{showEmpty ? "Hide empty fields" : `Show ${emptyCount} empty field${emptyCount > 1 ? "s" : ""}`}</button>}
    </div>
  );
}
